// Shared option sets and display helpers for the impact register.
//
// Deliberately small: the pillars and change actions are NOT listed here. They
// come from GET /insights/projects/<project_id>/impact-taxonomy/ so the
// vocabulary is the BE's — hardcoding a fourth action as "survey" (it is
// `assessment`) is exactly the drift this avoids. The one exception is
// CANONICAL_PILLAR_KEYS below, and that list decides translation only, never
// what the picker offers.

export const IMPACT_LEVELS = [
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
];

// Colours for the Level badge (High / Med / Low) — same palette as the
// stakeholder-group registry so the two screens read as one system.
export const LEVEL_BADGE = {
    high: { short: "High", color: "#B23A2E", bg: "rgba(255,120,90,0.18)" },
    medium: { short: "Med", color: "#8A5A00", bg: "rgba(237,108,2,0.16)" },
    low: { short: "Low", color: "#006622", bg: "rgba(0,143,45,0.15)" },
};

// Backends may return "medium" or "med" (etc.) — normalise to our canonical keys.
export const normalizeLevel = (value) => {
    if (!value) return null;
    const v = String(value).toLowerCase();
    if (v === "med" || v === "medium" || v === "mid") return "medium";
    if (v === "high" || v === "hi") return "high";
    if (v === "low" || v === "lo") return "low";
    return v;
};

// The `verification_status` vocabulary, in the order the column offers it:
// mirrored from the model's own VERIFICATION_CHOICES (impacts/models.py), not
// invented here. Read left to right it is the journey a row takes — nobody has
// checked it, somebody has, somebody disagrees — which is the order to offer in
// a menu.
//
// If the BE adds a fourth, this list is what has to grow: everything below
// already falls through gracefully for an unknown value (a neutral icon and a
// humanized label), so an unlisted status still RENDERS — it just cannot be
// chosen until it is named here.
export const VERIFICATION_STATUSES = ["unverified", "verified", "disputed"];

// Presentation for `verification_status`. The register used to only read this —
// "verification is its own task" — but the status of a row is exactly the kind
// of thing you fix while looking at the row, so the column is editable now and
// this is what its control paints with.
export const VERIFICATION_BADGE = {
    verified: { color: "#006622", bg: "rgba(0,143,45,0.15)" },
    unverified: { color: "text.secondary", bg: "rgba(0,0,0,0.06)" },
    disputed: { color: "#B23A2E", bg: "rgba(255,120,90,0.18)" },
};

// "needs_review" -> "Needs review". Used as the fallback label for a status the
// BE adds later that we have no explicit translation for.
export const humanizeStatus = (value) => {
    if (!value) return "";
    const text = String(value).replace(/[_-]+/g, " ").trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
};

// An endpoint may return a bare array or a paginated { results } shape.
export const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

// The taxonomy response, defensively: two arrays of {key,label,definition}, with
// `custom` on a row the project itself invented.
//
// `pillars` classify the impact (change_type); `edges` are the change actions
// (change_actions). The response still carries a `corners` array — the four
// Diamond corners this screen used to classify against — but it is DEPRECATED
// and deliberately dropped here so nothing can drift back onto it.
//
// `edges` is the framework's change actions first, then the project's own
// (`custom: true`).
export const asTaxonomy = (data) => ({
    pillars: Array.isArray(data?.pillars) ? data.pillars : [],
    edges: Array.isArray(data?.edges) ? data.edges : [],
});

// The empty taxonomy — the shape every holder of one starts from. Frozen because
// it is shared by every component that opens on one.
export const EMPTY_TAXONOMY = Object.freeze({
    pillars: [],
    edges: [],
});

// A stored key list, defensively — the BE stores change_type / change_actions as
// arrays of taxonomy keys, but an older row may hold a single string or null.
export const asKeys = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (typeof value === "string" && value) return [value];
    return [];
};

// Look a key up in a taxonomy list, falling back to the raw key so an impact
// saved against a key the taxonomy no longer offers still renders as something.
export const labelForKey = (options, key) =>
    options.find((option) => option.key === key)?.label || key;

// The six pillars every project starts with. These are Prompta's own vocabulary,
// so their display name is translated FE-side under `pillar.<key>` — the keys are
// the BE's and stable, which is what makes them safe to key a message id on.
//
// A project's own pillars (`custom: true`, e.g. "Change Fatigue") are NOT here:
// they are customer copy, shown verbatim in every locale.
export const CANONICAL_PILLAR_KEYS = [
    "vision",
    "success criteria",
    "culture",
    "capability",
    "leadership",
    "operations",
];

export const isCanonicalPillar = (key) => CANONICAL_PILLAR_KEYS.includes(String(key));

// A pillar key as the words a change person reads: the canonical six translated,
// a custom pillar verbatim, and a key the taxonomy no longer offers as itself
// rather than a blank cell.
export const pillarLabel = (intl, options, key) => {
    const fallback = labelForKey(options, key);
    if (!isCanonicalPillar(key)) return fallback;
    const id = `pillar.${key}`;
    return intl.messages?.[id] ? intl.formatMessage({ id }) : fallback;
};

// The picker's options, pre-translated, so the chips and the table agree on what
// a pillar is called. `custom` rides along — it is what the badge keys off.
export const pillarOptions = (intl, pillars) =>
    (pillars || []).map((pillar) => ({
        ...pillar,
        label: pillarLabel(intl, pillars, pillar.key),
        custom: Boolean(pillar.custom),
    }));

// The small uppercase heading over a section of the register — AREAS above the
// area tabs, GROUPS above the group registry. Shared so the two levels are
// visibly the same KIND of thing: one strip per level, each labelled the same
// way, which is what makes the area -> group -> impact hierarchy readable
// without a diagram.
export const SECTION_TITLE_SX = {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: "text.secondary",
    textTransform: "uppercase",
    mb: 0.75,
};
